'use client'

import { useState, useEffect, useCallback } from 'react'

export interface ChatSession {
  id: string
  title: string
  createdAt: number
  updatedAt: number
}

const STORAGE_KEY = 'sea_history'

export function useChatHistory() {
  const [sessions, setSessions] = useState<ChatSession[]>([])

  // Load sessions on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        setSessions(JSON.parse(stored))
      } catch (e) {
        console.error('Failed to parse chat history', e)
      }
    }
  }, [])

  const saveSession = useCallback((sessionId: string, messages: any[]) => {
    if (messages.length === 0) return

    setSessions(prev => {
      const existingIdx = prev.findIndex(s => s.id === sessionId)
      let newSessions = [...prev]

      // Extract title from first user message (supports both v4 content and v5 parts)
      const firstUserMsg = messages.find(m => m.role === 'user')
      let title = 'Nouvelle conversation'

      if (firstUserMsg) {
        // v5 format: parts array
        if (firstUserMsg.parts && Array.isArray(firstUserMsg.parts)) {
          const textPart = firstUserMsg.parts.find((p: any) => p.type === 'text')
          if (textPart?.text) title = textPart.text
        }
        // v4 format: content string or array
        else if (firstUserMsg.content) {
          if (typeof firstUserMsg.content === 'string') {
            title = firstUserMsg.content
          } else if (Array.isArray(firstUserMsg.content)) {
            const textPart = firstUserMsg.content.find((p: any) => p.type === 'text')
            if (textPart) title = textPart.text
          }
        }
      }

      const updatedSession: ChatSession = {
        id: sessionId,
        title: title.slice(0, 40),
        createdAt: existingIdx !== -1 ? prev[existingIdx].createdAt : Date.now(),
        updatedAt: Date.now()
      }

      if (existingIdx !== -1) {
        newSessions[existingIdx] = updatedSession
      } else {
        newSessions = [updatedSession, ...newSessions]
      }

      // Keep only the 20 most recent conversations (increased from xedu's 10)
      newSessions.sort((a, b) => b.updatedAt - a.updatedAt)
      if (newSessions.length > 20) {
        newSessions = newSessions.slice(0, 20)
      }

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newSessions))
      } catch (e) {
        console.error('Failed to save chat history', e)
      }
      return newSessions
    })
  }, [])

  const deleteSession = useCallback((sessionId: string) => {
    setSessions(prev => {
      const newSessions = prev.filter(s => s.id !== sessionId)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSessions))
      return newSessions
    })
  }, [])

  const clearHistory = useCallback(() => {
    setSessions([])
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  return { sessions, saveSession, deleteSession, clearHistory }
}
